import React, { Component, Fragment } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import PropTypes from 'prop-types';
import { injectIntl, defineMessages } from 'react-intl';
import { withRouter } from 'react-router-dom';
import orderBy from 'lodash.orderby';
import filter from 'lodash.filter';
import classNames from 'classnames';
import scrollToComponent from 'react-scroll-to-component';

import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';

import EventListItem from '../components/content/lists/EventListItem';
import OrderCard from '../components/content/OrderCard';
import withNotificationDialogConsumer from '../components/hoc/withNotificationDialogConsumer';
import { FormattedMessageWrappedInSpan } from '../components/misc';

import {
  NEW_SYSTEM_ORDER_REDIRECT_MSG,
  PAYMENT_COMPLETED,
  COMPLETED,
  PHOTO_DVD,
} from '../utils/variables';

const EVENTS_TAB = 0;
const ORDERS_TAB = 1;

class AccountPage extends Component {
  constructor(props) {
    super(props);

    const pendingOrders = filter(
      props.viewer.account.orders.edges,
      edge => edge.node.state !== PAYMENT_COMPLETED && edge.node.state !== COMPLETED
    );

    this.unpayedEvents = [];
    pendingOrders.forEach(pendingOrder => {
      pendingOrder.node.cart.lineItems.forEach(lineItem => {
        if (lineItem.product.type !== PHOTO_DVD) {
          this.unpayedEvents.push({
            ...lineItem.product.event,
            orderId: pendingOrder.node.orderid,
          });
        }
      });
    });

    this.state = {
      expandedOrderCardId: null,
      scrollToOrderCard: false,
      selectedTabIndex: null,
    };

    this.handleUnpayedEventClick = this.handleUnpayedEventClick.bind(this);
    this.handleOrderCardExpandClick = this.handleOrderCardExpandClick.bind(this);
    this.handleTabChange = this.handleTabChange.bind(this);
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    const nextTabIndex =
      nextProps.match.params.section === 'events' ? EVENTS_TAB : ORDERS_TAB;
    if (prevState.selectedTabIndex !== nextTabIndex) {
      return {
        selectedTabIndex: nextTabIndex,
      };
    }
    return null;
  }

  componentDidMount() {
    const { match, location, history, onOpenNotificationDialog } = this.props;
    if (match.params.section === 'orders') {
      if (location.search === '?old') {
        onOpenNotificationDialog(NEW_SYSTEM_ORDER_REDIRECT_MSG, () => {
          history.replace(location.pathname);
        });
      }
    }
  }

  componentDidUpdate() {
    if (this.state.scrollToOrderCard) {
      if (this.expandedOrderCard) {
        scrollToComponent(this.expandedOrderCard, { align: 'top' });
        this.setState({ scrollToOrderCard: false });
      }
    }
  }

  //since we're navigating to the same key, only the URL is changing
  handleTabChange(event, value) {
    switch (value) {
      case EVENTS_TAB:
        this.props.history.replace('/account/events');
        break;
      case ORDERS_TAB:
        this.props.history.replace('/account/orders');
        break;
      default:
        break;
    }
  }

  handleNavigateHome = () => {
    this.props.history.push('/');
  };

  handleUnpayedEventClick(eventId, orderId) {
    this.setState({ expandedOrderCardId: orderId, scrollToOrderCard: true });
    this.handleTabChange(null, ORDERS_TAB);
  }

  handleOrderCardExpandClick(orderId) {
    this.setState(currentState => ({
      expandedOrderCardId: currentState.expandedOrderCardId === orderId ? null : orderId,
      scrollToOrderCard: false,
    }));
  }

  _renderInfoBox() {
    return (
      <div className="info-box-container mt-50 mb-50">
        <h2 className="center-text">
          <FormattedMessageWrappedInSpan
            id="accountPage.infoBox.oldOrdersAccess.title"
            defaultMessage="You are looking for your photos from older orders?"
          />
        </h2>
        <p>
          <FormattedMessageWrappedInSpan
            id="accountPage.infoBox.oldOrdersAccess.description"
            defaultMessage="No problem. Your photos haven't disappeared. So far we have not migrated all photos to our new system. But you can log in via {link} with your old order number and email address to get your other orders."
            values={{
              link: (
                <a
                  href="https://www.sportograf.com/account/signin"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  www.sportograf.com
                </a>
              ),
            }}
          />
        </p>
      </div>
    );
  }

  render() {
    const { intl, viewer } = this.props;
    const { events, orders } = viewer.account;
    const { selectedTabIndex, expandedOrderCardId } = this.state;

    const orderedEvents = orderBy(events.edges, ['node.beginsAt'], ['desc']);
    const orderedOrders = orderBy(orders.edges, ['node.paymentReceivedAt'], ['desc']);

    //show call to action if customer has no previous orders
    if (orderedEvents.length === 0 && orderedOrders.length === 0) {
      return (
        <Fragment>
          <div className="row">
            <div className="col-xs-16 col-sm-10 col-sm-offset-3">
              <div className="account-page-no-orders">
                <h1>
                  <FormattedMessageWrappedInSpan
                    id="accountPage.emptyOrders.main"
                    defaultMessage="You have not placed any orders yet"
                  />
                </h1>
                <div>
                  <FormattedMessageWrappedInSpan
                    id="accountPage.emptyOrders.action"
                    defaultMessage="Feel free to browse the events and photos by clicking the button below"
                  />
                </div>
                <button onClick={this.handleNavigateHome}>
                  <FormattedMessageWrappedInSpan
                    id="accountPage.emptyOrders.actionButton"
                    defaultMessage="Browse events"
                  />
                </button>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-xs-16 col-sm-12 col-sm-offset-2">
              {this._renderInfoBox()}
            </div>
          </div>
        </Fragment>
      );
    }

    return (
      <div className="account-page-container container-960 container-page">
        <div className="row">
          <div className="col-xs-16 col-sm-12 col-sm-offset-2">
            <Tabs
              value={selectedTabIndex}
              onChange={this.handleTabChange}
              variant="fullWidth"
            >
              <Tab
                classes={{ root: 'account-page-tab' }}
                label={intl.formatMessage(translations.tabOne)}
                value={EVENTS_TAB}
              />
              <Tab
                classes={{ root: 'account-page-tab' }}
                label={intl.formatMessage(translations.tabTwo)}
                value={ORDERS_TAB}
              />
            </Tabs>
            {selectedTabIndex === EVENTS_TAB && (
              <Fragment>
                {orderedEvents.length !== 0 && (
                  <Fragment>
                    <h1>{intl.formatMessage(translations.tabOne)}</h1>
                    <div className="row">
                      {orderedEvents.map(edge => (
                        <EventListItem
                          event={edge.node}
                          hasFooter={false}
                          key={edge.node.id}
                          linkTo={`/account/event/${edge.node.sgId}`}
                        />
                      ))}
                    </div>
                  </Fragment>
                )}
                {this.unpayedEvents.length !== 0 && (
                  <Fragment>
                    <h1 className={classNames({ separator: orderedEvents.length !== 0 })}>
                      <FormattedMessageWrappedInSpan
                        id="accountPage.outstandingPayments.title"
                        defaultMessage="Outstanding Payments"
                      />
                    </h1>
                    <div className="row">
                      {this.unpayedEvents.map((event, index) => (
                        <EventListItem
                          bannerHasGrayScale={true}
                          event={event}
                          info={
                            <div>
                              <div className="event-list-item-date">
                                <p>
                                  <FormattedMessageWrappedInSpan
                                    id="accountPage.eventListItem.orderId"
                                    defaultMessage="Order ID:"
                                  />
                                </p>
                                <p>{event.orderId}</p>
                              </div>
                            </div>
                          }
                          hasFooter={false}
                          key={index}
                          onClick={eventId =>
                            this.handleUnpayedEventClick(eventId, event.orderId)
                          }
                        />
                      ))}
                    </div>
                  </Fragment>
                )}
              </Fragment>
            )}
            {selectedTabIndex === ORDERS_TAB && (
              <div className="order-container">
                <h1>{intl.formatMessage(translations.tabTwo)}</h1>
                {orderedOrders.map(order => {
                  const expanded = expandedOrderCardId === order.node.orderid;
                  return (
                    <OrderCard
                      expanded={expanded}
                      key={order.node.id}
                      order={order.node}
                      onExpandClick={this.handleOrderCardExpandClick}
                      ref={element => {
                        if (expanded) {
                          this.expandedOrderCard = element;
                        }
                      }}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </div>
        <div className="row">
          <div className="col-xs-16 col-sm-12 col-sm-offset-2">
            {this._renderInfoBox()}
          </div>
        </div>
      </div>
    );
  }
}

const translations = defineMessages({
  tabOne: {
    id: 'accountPage.tabs.tabOne',
    defaultMessage: 'Your photos',
  },
  tabTwo: {
    id: 'accountPage.tabs.tabTwo',
    defaultMessage: 'Your invoices',
  },
});

AccountPage.propTypes = {
  viewer: PropTypes.object,
  intl: PropTypes.object,
  match: PropTypes.object,
  history: PropTypes.object,
  location: PropTypes.object,
  onOpenNotificationDialog: PropTypes.func,
};

export { AccountPage };

export default createFragmentContainer(
  injectIntl(withRouter(withNotificationDialogConsumer(AccountPage))),
  {
    viewer: graphql`
      fragment AccountPage_viewer on Viewer {
        account {
          events(first: $count) {
            edges {
              node {
                id
                sgId
                ...EventListItem_event
              }
            }
          }
          orders(first: $count) {
            edges {
              node {
                id
                cart {
                  lineItems {
                    product {
                      event {
                        id
                        sgId
                        ...EventListItem_event
                      }
                      type
                    }
                  }
                }
                orderid
                state
                ...OrderCard_order
              }
            }
          }
        }
      }
    `,
  }
);
